import { useState } from "react";
import { Button, Table, Popconfirm, Message } from "@/frontend/components/common";
import { userService } from "@/frontend/services";
import type { PaginationRequestDTO, UserResponseDTO } from "@/shared/dto";
import { formatDate } from "@/shared/utils";
import { FaPlus, FaEdit, FaTrash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import { getUserStateInfo } from "@/frontend/utils/user";
import { usePagination, useDelete } from "@/frontend/hooks";
import { useAuthStore } from "@/frontend/stores";
import { UsersPermissionsGuardEnum } from "@/core/enums";
import { DatePicker, Select } from "antd";

const { RangePicker } = DatePicker;

const initialPagination: TablePaginationConfig = {
  pageSizeOptions: ["7","10", "20", "50", "100"],
  showSizeChanger: true,
  showTotal: (total) => `Total: ${total} registros`,
};

const initialParams: PaginationRequestDTO = { page: 1, pageSize: 7 };
export function UsersListPage() {
  const navigate = useNavigate();
  const { hasPermission, user: currentUser } = useAuthStore();
  const { data, loading, pagination, loadData } = usePagination<UserResponseDTO>({
    fetchFn: userService.getPaginate,
    initialParams: initialParams,
    initialPagination: initialPagination,
  });

  const [filterState, setFilterState] = useState<string | undefined>();
  const [dateRange, setDateRange] = useState<[string, string] | null>(null);

  function applyFilters(state?: string, range?: [string, string] | null) {
    loadData({
      page: 1,
      pageSize: 7,
      state,
      createdFrom: range?.[0],
      createdTo: range?.[1],
    });
  }

  const { handleDelete, showConfirm, confirmTitle, confirmDescription } = useDelete({
    deleteFn: userService.delete,
    loadData,
    successMessage: "Usuario eliminado exitosamente",
    showConfirm: true,
    confirmTitle: "¿Eliminar usuario?",
    confirmDescription: "Esta acción eliminará el usuario permanentemente. ¿Estás seguro?",
    errorMessage: "Error al eliminar usuario",
  });

  const handleDeleteWithValidation = (userId: string) => {
    // Validar que el usuario no se elimine a sí mismo
    if (currentUser && currentUser.id === userId) {
      Message.error({ content: "No puedes eliminar tu propio usuario" });
      return;
    }
    handleDelete(userId);
  };


  const columns: ColumnsType<UserResponseDTO> = [
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Nombre Completo",
      dataIndex: "fullName",
      key: "fullName",
    },
    {
      title: "Estado",
      dataIndex: "state",
      key: "state",
      render: (state: string) => {
        const stateInfo = getUserStateInfo(state);
        return (
          <span className={stateInfo.color}>
            {stateInfo.label}
          </span>
        );
      },
    },
    {
      title: "Fecha de Creación",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date: Date) => formatDate(date),
    },
    {
      title: "Acciones",
      key: "actions",
      width: 150,
      render: (_: any, record: UserResponseDTO) => (
        <div className="flex gap-2">
          <Button
            type="link"
            disabled={!hasPermission(UsersPermissionsGuardEnum.USERS_UPDATE)}
            icon={<FaEdit />}
            onClick={() => navigate(`/users/edit/${record.id}`)}
          >
            Editar
          </Button>
          {showConfirm ? (
            <Popconfirm
              title={confirmTitle}
              description={confirmDescription}
              onConfirm={() => handleDeleteWithValidation(record.id)}
              okText="Sí, eliminar"
              cancelText="Cancelar"
              okButtonProps={{ danger: true }}
            >
              <Button
                type="link"
                danger
                disabled={!hasPermission(UsersPermissionsGuardEnum.USERS_DELETE) || (currentUser?.id === record.id)}
                icon={<FaTrash />}
              >
                Eliminar
              </Button>
            </Popconfirm>
          ) : (
            <Button
              type="link"
              danger
              disabled={!hasPermission(UsersPermissionsGuardEnum.USERS_DELETE) || (currentUser?.id === record.id)}
              icon={<FaTrash />}
              onClick={() => handleDeleteWithValidation(record.id)}
            >
              Eliminar
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="bg-white rounded-lg p-6 shadow-sm">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Gestión de Usuarios</h2>
          <p className="text-gray-600 mt-1">
            Administra los usuarios de la plataforma de torneos
          </p>
        </div>
        <Button
          type="primary"
          icon={<FaPlus />}
          onClick={() => navigate("/users/create")}
        >
          Crear Usuario
        </Button>
      </div>

      <div className="flex gap-3 mb-4">
        <Select
          placeholder="Estado"
          allowClear
          value={filterState}
          onChange={(val) => {
            setFilterState(val);
            applyFilters(val, dateRange);
          }}
          options={[
            { label: "Activo", value: "active" },
            { label: "Inactivo", value: "inactive" },
          ]}
          style={{ width: 150 }}
        />

        <RangePicker
          placeholder={["Fecha inicio", "Fecha fin"]}
          onChange={(_, dateStrings) => {
            const range = dateStrings && dateStrings[0] && dateStrings[1]
              ? [dateStrings[0], dateStrings[1]] as [string, string]
              : null;
            setDateRange(range);
            applyFilters(filterState, range);
          }}
        />
      </div>

      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        rowKey="id"
        pagination={pagination}
      />
    </div>
  );
}
